import React from 'react';
import { Mail, Phone, Shield, Calendar, LogIn, UserCheck, FileCheck, UserPlus, Hash } from 'lucide-react';
import { AdminUserOverview } from '../../../services/adminUserService';

interface AdminUserContactCardProps {
  overview: AdminUserOverview;
}

export function AdminUserContactCard({ overview }: AdminUserContactCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Contact Details</h2>
          <p className="text-sm text-gray-500">Direct information to reach this user.</p>
        </div>
        <StatusPill isBlocked={overview.isBlocked} emailConfirmed={Boolean(overview.emailConfirmedAt)} ficaVerified={overview.ficaVerified} />
      </div>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoRow icon={<UserCheck className="w-4 h-4" />} label="Full Name" value={overview.fullName || 'Not provided'} />
        <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={overview.email} />
        <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone" value={overview.phone || 'Not provided'} />
        <InfoRow icon={<Shield className="w-4 h-4" />} label="Role" value={overview.role} />
        <InfoRow icon={<FileCheck className="w-4 h-4" />} label="FICA Verified" value={overview.ficaVerified ? 'Yes' : 'No'} />
        <InfoRow icon={<Calendar className="w-4 h-4" />} label="Created" value={formatDate(overview.createdAt)} />
        <InfoRow icon={<LogIn className="w-4 h-4" />} label="Last Sign In" value={overview.lastSignInAt ? formatDate(overview.lastSignInAt) : 'Never'} />
        {overview.referrerId ? (
          <InfoRow
            icon={<UserPlus className="w-4 h-4" />}
            label="Referred By"
            value={
              <div className="space-y-1">
                <div className="font-medium">{overview.referrerName || overview.referrerEmail || 'Unknown'}</div>
                {overview.referrerAffiliateCode && (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Hash className="w-3 h-3" />
                    <span>{overview.referrerAffiliateCode}</span>
                  </div>
                )}
                {overview.referrerEmail && overview.referrerEmail !== overview.referrerName && (
                  <div className="text-xs text-gray-500">{overview.referrerEmail}</div>
                )}
              </div>
            }
          />
        ) : (
          <InfoRow icon={<UserPlus className="w-4 h-4" />} label="Referred By" value="Direct signup" />
        )}
      </dl>
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600">
        {icon}
      </span>
      <div>
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 mt-1 break-words">{value}</dd>
      </div>
    </div>
  );
}

interface StatusPillProps {
  isBlocked: boolean;
  emailConfirmed: boolean;
  ficaVerified: boolean;
}

function StatusPill({ isBlocked, emailConfirmed, ficaVerified }: StatusPillProps) {
  if (isBlocked) {
    return <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Blocked</span>;
  }
  
  // Show multiple status badges
  return (
    <div className="flex gap-2">
      {emailConfirmed && (
        <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Email Verified</span>
      )}
      {ficaVerified ? (
        <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">FICA Verified</span>
      ) : (
        <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">FICA Pending</span>
      )}
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}

